import { membersService } from '@services/domain-factories/members';
import { normalizeRolePermissions } from '@services/domain-factories/roles';
import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { Member, Role, User } from '@vincerosoft/assessfluence-types';
import { limit, where } from 'firebase/firestore';

function userFactory() {
  async function getUserByEmail(email: string) {
    const users = await collectionsService.users.getDocs<User>([
      where('email', '==', email),
      limit(1)
    ]);
    return users.length > 0 ? users[0] : null;
  }

  async function getUserByUid(uid: string) {
    const users = await collectionsService.users.getDocs<User>(where('uid', '==', uid), {
      forceServer: true
    });
    return users.length > 0 ? users[0] : null;
  }

  async function updateUserAppVersion(userId: string) {
    await collectionsService.users.updateDoc(userId, {
      app_version: import.meta.env.VITE_APP_VERSION
    });
  }

  async function initUserContext(uid: string) {
    const inFlightStore: {
      appUser: User | null;
      currentMember: Member | null;
      memberAccounts: Member[] | null;
      role: Role | null;
    } = {
      appUser: null,
      currentMember: null,
      memberAccounts: null,
      role: null
    };

    const user = await getUserByUid(uid);

    if (!user) {
      throw new Error('User not found');
    }

    if (user.status !== 'active') {
      throw new Error('User status is not active');
    }

    if (!user.member_ids) {
      throw new Error('User has no member ids');
    }

    inFlightStore.appUser = user;

    const members = await membersService.getMembersByUserId(user.id);

    if (members.length === 0) {
      throw new Error('Member not found');
    }
    const validMembers = members.filter((member) => member.status === 'active');

    if (validMembers.length === 0) {
      throw new Error('Member status is not active');
    }

    inFlightStore.memberAccounts = validMembers;

    let lastSignedInUser = validMembers.find((mem) => mem.id === user.current_member_id);

    if (!lastSignedInUser) {
      lastSignedInUser = validMembers.find((mem) => mem.id === user.default_member_id);

      if (!lastSignedInUser) {
        lastSignedInUser = validMembers[0];
      }
    }

    const member = lastSignedInUser;

    if (!member) {
      throw new Error('No member found for user');
    }

    inFlightStore.currentMember = member;
    const role = await collectionsService.roles.getDoc(member.role_id);

    role.permissions = normalizeRolePermissions(role);
    inFlightStore.role = role;

    return inFlightStore;
  }

  return {
    getUserByEmail,
    getUserByUid,
    updateUserAppVersion,
    initUserContext
  };
}

export const userService = userFactory();
